// Converts ONAV and ASPI sheets (and Mercadini wine profiles) into the WINE parameters of a Mercadini pairing sheet (scale 0-10).
// This file has no imports on purpose, so it can be tested with plain node (see onavToPairing.test.mjs).
//
// ONAV (scale 0-8 → 0-10; the sparkling variant of the sheet is scored out of 7 → ×10/7), direct conversions:
//   gustatory_body         → wine_body
//   olfactory_intensity    → wine_olfactory_intensity
//   gustatory_persistence  → wine_pai (the ONAV persistence is used as a proxy of the PAI)
// ASPI (scale 1-5 → ×2 = 2-10), direct conversions:
//   gustatory_sugars → wine_sweetness      gustatory_alcohols → wine_alcohol
//   gustatory_polyols → wine_softness      gustatory_acids → wine_acidity
//   gustatory_tannins → wine_tannicity     gustatory_minerals → wine_sapidity
//   olfactory_intensity → wine_olfactory_intensity     gustatory_persistence → wine_pai
//   (the body is only available on ONAV sheets)
// Estimates (from the wine data, both sheet types):
//   abv        → wine_alcohol       (10% vol = 0, +2 points per degree, max 10)
//   wine_type  → wine_effervescence (sparkling 8, semi_sparkling 5, other types 0; ASPI bubble grain ±1 on sparkling)
//   wine_type  → wine_sweetness     (passito only: 8)
// Mercadini wine profile ("scheda vino Mercadini", type 'profile'): its ten wine_* values are already on the 0-10 scale and are used as they are.
// For ONAV and ASPI sheets a mark equal to 0 or missing counts as "not filled in" and is not imported. In a Mercadini profile
// (schema v10) a parameter is "not filled in" only when it is null: a 0 is a real value.
// NO averaging: values always come from ONE sheet. A second import can only COMPLETE the parameters that are still empty
// (see completeValues); "not filled in" (null) is never turned into 0 and never enters any calculation.

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// A parameter is filled in when it holds a number (0 included); null, undefined, '' and non numeric values are "not filled in"
export const isFilled = (value) => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));
const scale8 = (v) => clamp(Math.round((v * 10) / 8), 0, 10);
const scale7 = (v) => clamp(Math.round((v * 10) / 7), 0, 10);
// The marks used here (body, olfactory intensity, persistence) are out of 8 on the still ONAV form and out of 7 on the sparkling one
const onavScale = (sheet) => (sheet.variant === 'sparkling' ? scale7 : scale8);
const scale5 = (v) => clamp(Math.round(v * 2), 0, 10);

// The ten wine parameters of the Mercadini sheet
export const WINE_PARAM_FIELDS = [
  'wine_sweetness', 'wine_acidity', 'wine_sapidity', 'wine_effervescence', 'wine_tannicity',
  'wine_alcohol', 'wine_softness', 'wine_olfactory_intensity', 'wine_body', 'wine_pai',
];

// Parameters an ONAV sheet alone cannot give (free text there). With ASPI sheets they are direct conversions.
export const MANUAL_WINE_FIELDS = ['wine_acidity', 'wine_sapidity', 'wine_tannicity', 'wine_softness'];

const ASPI_DIRECT = {
  gustatory_sugars: 'wine_sweetness',
  gustatory_alcohols: 'wine_alcohol',
  gustatory_polyols: 'wine_softness',
  gustatory_acids: 'wine_acidity',
  gustatory_tannins: 'wine_tannicity',
  gustatory_minerals: 'wine_sapidity',
  olfactory_intensity: 'wine_olfactory_intensity',
  gustatory_persistence: 'wine_pai',
};

// Estimates shared by both sheet types (wine data): adds to values/estimated unless a direct value exists
function addWineDataEstimates(sheet, values, direct, estimated) {
  if (sheet.abv > 0 && !direct.includes('wine_alcohol')) {
    values.wine_alcohol = clamp(Math.round((sheet.abv - 10) * 2), 0, 10);
    estimated.push('wine_alcohol');
  }
  if (sheet.wine_type) {
    let effervescence = sheet.wine_type === 'sparkling' ? 8 : sheet.wine_type === 'semi_sparkling' ? 5 : 0;
    if (sheet.wine_type === 'sparkling') {
      if (sheet.bubble_grain === 'very_fine') effervescence += 1;
      else if (sheet.bubble_grain === 'coarse') effervescence -= 1;
    }
    values.wine_effervescence = effervescence;
    estimated.push('wine_effervescence');
    if (sheet.wine_type === 'passito' && !direct.includes('wine_sweetness')) {
      values.wine_sweetness = 8;
      estimated.push('wine_sweetness');
    }
  }
}

// sheet: ONAV API object (flattened wine fields included). Returns { values, direct, estimated, manual }.
// A sheet of the sparkling variant without a wine type is estimated like a sparkling wine (effervescence 8).
export function onavToWineParams(sheet) {
  const values = {};
  const direct = [];
  const estimated = [];

  const scale = onavScale(sheet);
  if (sheet.gustatory_body > 0) { values.wine_body = scale(sheet.gustatory_body); direct.push('wine_body'); }
  if (sheet.olfactory_intensity > 0) { values.wine_olfactory_intensity = scale(sheet.olfactory_intensity); direct.push('wine_olfactory_intensity'); }
  if (sheet.gustatory_persistence > 0) { values.wine_pai = scale(sheet.gustatory_persistence); direct.push('wine_pai'); }

  addWineDataEstimates(sheet.variant === 'sparkling' && !sheet.wine_type ? { ...sheet, wine_type: 'sparkling' } : sheet, values, direct, estimated);

  const manual = [...MANUAL_WINE_FIELDS];
  if (values.wine_sweetness === undefined) manual.push('wine_sweetness');
  return { values, direct, estimated, manual };
}

// sheet: ASPI API object (flattened wine fields included). Returns { values, direct, estimated, manual }.
export function aspiToWineParams(sheet) {
  const values = {};
  const direct = [];
  const estimated = [];

  Object.entries(ASPI_DIRECT).forEach(([field, param]) => {
    if (sheet[field] > 0) { values[param] = scale5(sheet[field]); direct.push(param); }
  });

  addWineDataEstimates(sheet, values, direct, estimated);

  const manual = WINE_PARAM_FIELDS.filter((field) => values[field] === undefined);
  return { values, direct, estimated, manual };
}

// sheet: Mercadini wine profile API object (flattened wine_* keys). Returns { values, direct, estimated, manual }.
// A parameter holding a number (0 included) is filled in; null/missing stays not filled in (and is listed in `manual`).
export function profileToWineParams(sheet) {
  const values = {};
  const direct = [];
  WINE_PARAM_FIELDS.forEach((field) => {
    if (!isFilled(sheet[field])) return;
    const value = Number(sheet[field]);
    if (value < 0) return;
    values[field] = clamp(Math.round(value), 0, 10);
    direct.push(field);
  });
  const manual = WINE_PARAM_FIELDS.filter((field) => values[field] === undefined);
  return { values, direct, estimated: [], manual };
}

export const sheetToWineParams = (sheet) => {
  if (sheet.type === 'aspi') return aspiToWineParams(sheet);
  if (sheet.type === 'profile') return profileToWineParams(sheet);
  return onavToWineParams(sheet);
};

// What an import does with the ten parameters (one sheet at a time, no averaging).
// `values` are the parameters the sheet really gives (see sheetToWineParams); `current` the values of the form.
// * replaceValues: every parameter takes the sheet's value; the ones the sheet does not give become null (not filled in).
// * completeValues: only the parameters that are still empty in `current` are filled; nothing is ever overwritten.
//   Returns { values: <full set of the ten parameters>, filled: [fields that were completed] }.
export function replaceValues(values) {
  const out = {};
  WINE_PARAM_FIELDS.forEach((field) => { out[field] = values[field] === undefined ? null : values[field]; });
  return out;
}

export function completeValues(current, values) {
  const out = {};
  const filled = [];
  WINE_PARAM_FIELDS.forEach((field) => {
    if (isFilled(current[field])) out[field] = Number(current[field]);
    else if (values[field] !== undefined) { out[field] = values[field]; filled.push(field); }
    else out[field] = null;
  });
  return { values: out, filled };
}

// Parameters still empty after an import, from the full set of values
export const emptyParams = (full) => WINE_PARAM_FIELDS.filter((field) => !isFilled(full[field]));
